import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  AwaitingBanner,
  RangeSelect,
  pct,
} from "./adminUi";
import { CheckCircle2, XCircle, Info } from "lucide-react";

const RANGES = [
  ["today", "Today"],
  ["7d", "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
  ["", "All time"],
];

export default function AdminProverbAnalyticsPage() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [topics, setTopics] = useState(null);

  useEffect(() => {
    setData(null);
    adminApi.proverbAnalytics({ range }).then(setData).catch(() => setData({ status: "awaiting_integration" }));
  }, [range]);
  useEffect(() => {
    adminApi.proverbStatus().then(setStatus).catch(() => {});
    adminApi.topicInsights({ range: "30d" }).then(setTopics).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-proverb-page" className="space-y-8">
      <PageHeader
        eyebrow="Cross-product analytics"
        title="I Need a Proverb"
        subtitle="Traffic, search behavior, engagement, and cross-product conversion into LifeGuide."
        right={<RangeSelect value={range} onChange={setRange} options={RANGES} testid="proverb-range" />}
      />

      {/* --- Current Integration Health ------------------------------- */}
      <Card
        data-testid="proverb-current-health-card"
        className={`border ${
          status
            ? status.currently_healthy
              ? "border-[#4F6F52]/50"
              : "border-[#B4795C]/50"
            : "border-white/10"
        }`}
      >
        <div className="flex items-start gap-3">
          {status?.currently_healthy ? (
            <CheckCircle2 size={20} className="text-[#8FBF87] mt-0.5" />
          ) : status?.connected ? (
            <XCircle size={20} className="text-[#F3D6A6] mt-0.5" />
          ) : (
            <XCircle size={20} className="text-[#B4795C] mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#B7D2AF] mb-1">
              Current integration health
            </div>
            <div
              data-testid="proverb-current-health-label"
              className={`text-lg font-display leading-tight ${
                status?.currently_healthy
                  ? "text-[#8FBF87]"
                  : status?.connected
                  ? "text-[#F3D6A6]"
                  : "text-[#B4795C]"
              }`}
            >
              {!status
                ? "Loading…"
                : status.currently_healthy
                ? "Healthy — receiving events"
                : status.connected
                ? "New failures since last success"
                : "Awaiting integration"}
            </div>
            <div
              className="mt-1.5 text-xs text-[#B7D2AF] leading-relaxed"
              data-testid="proverb-current-health-detail"
            >
              {!status ? (
                "Checking…"
              ) : status.currently_healthy ? (
                <>
                  No new ingest failures since the last successful event.
                  {status.last_success_at && (
                    <>
                      {" "}Last successful event at{" "}
                      <span className="text-white">
                        {String(status.last_success_at)
                          .slice(0, 19)
                          .replace("T", " ")}
                      </span>{" "}
                      UTC.
                    </>
                  )}
                </>
              ) : status.connected ? (
                <>
                  <span className="text-white">
                    {status.failures_since_last_success}
                  </span>{" "}
                  new failure{status.failures_since_last_success === 1 ? "" : "s"} since the last successful event
                  {status.last_success_at && (
                    <>
                      {" "}({String(status.last_success_at).slice(0, 19).replace("T", " ")} UTC)
                    </>
                  )}
                  .
                </>
              ) : (
                "No successful events received yet. Verify PROVERB_INGEST_SECRET and try one signed test payload."
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-[#8FA894]">
              <span data-testid="proverb-health-secret">
                secret {status?.secret_configured ? "configured" : "missing"}
              </span>
              <span>·</span>
              <span data-testid="proverb-health-counts">
                <span className="text-white">{status?.event_count ?? 0}</span> prod ·{" "}
                <span className="text-white">{status?.test_event_count ?? 0}</span> test
              </span>
              <span>·</span>
              <span>
                Throughput{" "}
                <span className="text-white">
                  {status?.throughput?.last_24h ?? 0}
                </span>{" "}
                / 24h ·{" "}
                <span className="text-white">
                  {status?.throughput?.last_7d ?? 0}
                </span>{" "}
                / 7d
              </span>
              <span>·</span>
              <span data-testid="proverb-health-since">
                Failures since last success:{" "}
                <span
                  className={
                    (status?.failures_since_last_success ?? 0) === 0
                      ? "text-[#8FBF87]"
                      : "text-[#F3D6A6]"
                  }
                >
                  {status?.failures_since_last_success ?? 0}
                </span>
                {" · 1h "}
                <span className="text-white">{status?.failures_last_1h ?? 0}</span>
                {" · 24h "}
                <span className="text-white">{status?.failures_last_24h ?? 0}</span>
              </span>
            </div>
            {status?.last_event && (
              <div
                className="mt-1.5 text-[11px] text-[#8FA894]"
                data-testid="proverb-last-event"
              >
                Last event:{" "}
                <span className="text-white">
                  {String(status.last_event.event_type)}
                </span>{" "}
                ·{" "}
                {String(status.last_event.created_at)
                  .slice(0, 19)
                  .replace("T", " ")}{" "}
                UTC
              </div>
            )}
          </div>
        </div>

        {status?.type_breakdown_24h &&
          Object.keys(status.type_breakdown_24h).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {Object.entries(status.type_breakdown_24h).map(([k, v]) => (
                <span
                  key={k}
                  className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#B7D2AF]"
                >
                  {k}: <span className="text-white ml-1">{v}</span>
                </span>
              ))}
            </div>
          )}

        <details className="mt-4">
          <summary className="text-[11px] text-[#B7D2AF] cursor-pointer">
            Integration contract for the I Need a Proverb team
          </summary>
          <div className="mt-2 text-[11px] text-[#8FA894] leading-relaxed space-y-0.5">
            <div>
              <span className="text-white">Endpoint:</span>{" "}
              <code>POST /api/analytics/proverb/ingest</code> (single) or{" "}
              <code>/ingest/batch</code> (up to 250 events).
            </div>
            <div>
              <span className="text-white">Auth:</span> HMAC-SHA256 of the raw
              body using <code>PROVERB_INGEST_SECRET</code>, sent as{" "}
              <code>X-Proverb-Signature: sha256=&lt;hex&gt;</code>.
            </div>
            <div>
              <span className="text-white">Idempotency:</span> supply a UUID as{" "}
              <code>event_id</code> — duplicates are silently deduped.
            </div>
            <div>
              <span className="text-white">Event types:</span> visit · search ·
              result_view · share · daily_signup · lifeguide_click ·
              lifeguide_referral · lifeguide_conversion.
            </div>
            <div>
              <span className="text-white">Sponsor attribution:</span> include
              any of <code>referral_code</code> / <code>sponsor_slug</code> /{" "}
              <code>sponsor_id</code> — mirrored into <code>sponsor_events</code>.
            </div>
            <div>
              <span className="text-white">Test filter:</span> set{" "}
              <code>metadata.env</code> to preview/staging/dev/test OR{" "}
              <code>metadata.origin</code> to test/seed to keep events out of
              production KPIs.
            </div>
            <div className="mt-1">
              Full schema at <code>GET /api/analytics/proverb/schema</code>.
            </div>
          </div>
        </details>
      </Card>

      {/* --- Historical Ingest Errors (audit log) ---------------------- */}
      {status?.recent_failures?.length > 0 && (
        <Card
          data-testid="proverb-historical-failures-card"
          className="border border-white/10"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#B4795C] mb-1">
                Historical ingest errors
              </div>
              <div className="text-sm text-[#B7D2AF] leading-relaxed">
                Audit trail — never cleared. Rows that pre-date the last
                successful event are labelled <em>Historical</em>. A row
                without that label indicates a real, current problem.
              </div>
            </div>
            {status.last_failure_at && (
              <div
                className="text-[11px] text-[#8FA894] whitespace-nowrap"
                data-testid="proverb-last-failure-at"
              >
                Last failure:{" "}
                <span className="text-white">
                  {String(status.last_failure_at).slice(0, 19).replace("T", " ")}
                </span>{" "}
                UTC
              </div>
            )}
          </div>
          <ul className="mt-4 divide-y divide-white/5">
            {status.recent_failures.map((f, i) => (
              <li
                key={i}
                data-testid={`proverb-failure-row-${i}`}
                className="py-2 flex items-center justify-between gap-3 text-[11px]"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[#F3D6A6] font-mono truncate">
                    {f.reason}
                  </div>
                  <div className="text-[#7C8F7C] mt-0.5">
                    {String(f.created_at).slice(0, 19).replace("T", " ")} UTC
                  </div>
                </div>
                {f.is_historical ? (
                  <span
                    data-testid={`proverb-failure-badge-historical-${i}`}
                    className="shrink-0 text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#8FA894]"
                  >
                    Historical
                  </span>
                ) : (
                  <span
                    data-testid={`proverb-failure-badge-new-${i}`}
                    className="shrink-0 text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-[#241914] border border-[#B4795C]/50 text-[#F3D6A6]"
                  >
                    New — since last success
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data && data.status !== "connected" ? (
        <AwaitingBanner label="I Need a Proverb analytics — awaiting integration" />
      ) : null}

      {data && data.status === "connected" && (
        <>
          <section>
            <SectionHeader label="Traffic" />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              <Kpi testid="prv-visitors" label="Total visitors" value={data.traffic?.total_visitors} />
              <Kpi testid="prv-unique" label="Unique visitors" value={data.traffic?.unique_visitors} />
              <Kpi testid="prv-returning" label="Returning visitors" value={data.traffic?.returning_visitors} />
            </div>
          </section>

          <section>
            <SectionHeader label="Search behavior" />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              <Kpi testid="prv-searches" label="Total searches" value={data.search?.total_searches} />
              <Kpi
                testid="prv-spv"
                label="Searches / visitor"
                value={data.search?.searches_per_visitor}
              />
            </div>
            {data.search?.top_terms?.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-[0.16em] text-[#8FA894] mb-2">
                  Top anonymous search terms
                </div>
                <Card>
                  <ul className="text-sm text-[#E7ECE4] grid sm:grid-cols-2 gap-y-1">
                    {data.search.top_terms.map((t) => (
                      <li key={t.term} className="flex justify-between border-b border-white/5 py-1">
                        <span className="truncate pr-2">{t.term}</span>
                        <span className="text-[#8FA894]">{t.count}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            )}
          </section>

          <section>
            <SectionHeader label="Content engagement" />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              <Kpi testid="prv-results" label="Results viewed" value={data.engagement?.results_viewed} />
              <Kpi testid="prv-shares" label="Shares" value={data.engagement?.shares} />
              <Kpi testid="prv-shares-email" label="Email shares" value={data.engagement?.email_shares} />
              <Kpi testid="prv-shares-sms" label="SMS shares" value={data.engagement?.sms_shares} />
              <Kpi testid="prv-shares-anon" label="Anonymous shares" value={data.engagement?.anon_shares} />
              <Kpi testid="prv-daily-signups" label="Daily Proverb signups" value={data.engagement?.daily_signups} />
            </div>
          </section>

          <section>
            <SectionHeader label="Cross-product activity" />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              <Kpi testid="prv-lg-clicks" label="LifeGuide clicks" value={data.cross_product?.lifeguide_clicks} />
              <Kpi testid="prv-lg-signups" label="LifeGuide signups" value={data.cross_product?.lifeguide_signups} />
              <Kpi testid="prv-lg-trials" label="Pro trials" value={data.cross_product?.lifeguide_trials} />
              <Kpi testid="prv-lg-paid" label="Paid Pro" value={data.cross_product?.lifeguide_paid} tone="accent" />
            </div>
          </section>

          <section>
            <SectionHeader label="Conversion rates" />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              <Kpi testid="prv-r-vs" label="Visit → Search" value={data.conversions?.visit_to_search_pct == null ? "—" : `${data.conversions.visit_to_search_pct}%`} />
              <Kpi testid="prv-r-sv" label="Search → Result" value={data.conversions?.search_to_view_pct == null ? "—" : `${data.conversions.search_to_view_pct}%`} />
              <Kpi testid="prv-r-vc" label="Visit → LG Click" value={data.conversions?.visit_to_lg_click_pct == null ? "—" : `${data.conversions.visit_to_lg_click_pct}%`} />
              <Kpi testid="prv-r-cs" label="Click → Signup" value={data.conversions?.click_to_signup_pct == null ? "—" : `${data.conversions.click_to_signup_pct}%`} />
              <Kpi testid="prv-r-st" label="Signup → Trial" value={data.conversions?.signup_to_trial_pct == null ? "—" : `${data.conversions.signup_to_trial_pct}%`} />
              <Kpi testid="prv-r-tp" label="Trial → Paid" value={data.conversions?.trial_to_paid_pct == null ? "—" : `${data.conversions.trial_to_paid_pct}%`} />
            </div>
          </section>
        </>
      )}

      <section>
        <SectionHeader label="What people need help with (last 30 days)" />
        {topics == null ? (
          <div className="text-sm text-[#8FA894]">Loading…</div>
        ) : topics.total === 0 ? (
          <AwaitingBanner label="No aggregate topic data in this window yet" />
        ) : (
          <Card>
            <div className="text-[11px] text-[#8FA894] mb-3">
              {topics.total} classified queries across LifeGuide + I Need a Proverb.{" "}
              {topics.note}
            </div>
            <ul className="grid sm:grid-cols-2 gap-y-1 text-sm" data-testid="topic-insights-list">
              {topics.topics.map((t) => (
                <li key={t.topic} className="flex items-center justify-between border-b border-white/5 py-1.5">
                  <span className="capitalize text-[#E7ECE4]">{t.topic.replace("_", " ")}</span>
                  <span className="text-[#8FA894] text-xs">
                    {t.count} · {t.pct_of_total}%
                    {t.trend_vs_prior_pct != null && (
                      <span className={` ml-2 ${t.trend_vs_prior_pct >= 0 ? "text-[#8FBF87]" : "text-[#D28C6E]"}`}>
                        {t.trend_vs_prior_pct >= 0 ? "▲" : "▼"} {Math.abs(t.trend_vs_prior_pct)}%
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}
